import React, { useState } from "react";
import { Factory, CheckCircle2, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const empty = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  product_requirement: "",
  quantity: "",
  city: "",
  message: "",
  hp: "",
};

export default function Enquiry() {
  const [form, setForm] = useState(empty);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.phone && !form.email) {
      setError("Please provide a phone number or email so we can reach you.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/leads", form);
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2.5 justify-center mb-6">
          <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Factory className="h-5 w-5 text-white" />
          </div>
          <span className="font-display font-semibold text-lg text-slate-900">Lami Tube Industries</span>
        </div>

        {sent ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="enquiry-success">
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-semibold text-slate-900">Thank you!</h1>
            <p className="text-slate-500 mt-2">Your enquiry has been received. Our team will get back to you shortly with a quotation.</p>
            <Button className="mt-6 bg-blue-600 hover:bg-blue-700" onClick={() => { setForm(empty); setSent(false); }} data-testid="enquiry-new">
              Submit another enquiry
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-7" data-testid="enquiry-form">
            <h1 className="font-display text-2xl font-semibold text-slate-900">Request a Quote</h1>
            <p className="text-slate-500 text-sm mt-1">Tell us your laminated tube requirement and we'll send you a quotation.</p>

            {/* honeypot - hidden from humans */}
            <input type="text" value={form.hp} onChange={(e) => set("hp", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <div className="grid sm:grid-cols-2 gap-3.5 mt-6">
              <div className="sm:col-span-2">
                <Label className="text-xs">Company Name</Label>
                <Input data-testid="enquiry-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Your company" />
              </div>
              <div>
                <Label className="text-xs">Your Name</Label>
                <Input data-testid="enquiry-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91..." />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Email</Label>
                <Input data-testid="enquiry-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Product Requirement</Label>
                <Input data-testid="enquiry-product" value={form.product_requirement} onChange={(e) => set("product_requirement", e.target.value)} placeholder="e.g. D35 PBL tubes" />
              </div>
              <div>
                <Label className="text-xs">Quantity</Label>
                <Input data-testid="enquiry-quantity" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="e.g. 50,000 pcs" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">City</Label>
                <Input data-testid="enquiry-city" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Message</Label>
                <Textarea data-testid="enquiry-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Any specifications or notes…" />
              </div>
            </div>

            {error && <p className="text-sm text-red-600 mt-3" data-testid="enquiry-error">{error}</p>}

            <Button type="submit" disabled={loading} className="w-full mt-5 bg-blue-600 hover:bg-blue-700 h-11" data-testid="enquiry-submit">
              {loading ? "Sending…" : <>Send Enquiry <Send className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </form>
        )}
        <p className="text-center text-xs text-slate-400 mt-5">Powered by Lami Tube Lead Funnel</p>
      </div>
    </div>
  );
}
